import { inject, Injectable } from '@angular/core';
import {
  AlarmConfigParam,
  bodyToConfig,
  CommonOption,
  DataAdministrationService,
  Filter,
  GetCentersParam,
  GetDistributorDefalutParam,
  GetDistributorDefaultResponse,
  GetLinesParam,
  GetTrafosParam,
  RegularOption,
  UpdateDataDistributorConfigParam
} from '@data';
import { map, Observable } from 'rxjs';
import { ErrorHandlerService } from '@errorHandler';
import { ERROR_KEY } from '../core/constants/error-messages.constants';;


/**
 * AdministrationService handles the logic for fetching and transforming data configurations of alarms and their infrastructure.
 */
@Injectable({ providedIn: 'root' })
export class AdministrationService {
  protected readonly dataAdministrationService = inject(DataAdministrationService);
  protected readonly errorService = inject(ErrorHandlerService);
  protected readonly fallbackValueGetDistributorDefaultResponse:GetDistributorDefaultResponse = {
    "id": "",
    "distributorId": "",
    "centerId": "",
    "transformerId": "",
    "lineId": "",
    "deviceType": "",
    "config": [
        {
            "alarmCategory": '',
            "alarmType": '',
            "priority": '',
            "duration": 0,
            "durationUnit": '',
            "referenceValue": 0,
            "referenceValueUnit": '',
            "value_min": 0,
            "valueMinOperator": '',
            "valueMax": undefined,
            "valueMaxOperator": undefined,
            "valuePercentage": false,
        }
    ],
    "enabled": true,
    "audit": {
        "insertionUser": "",
        "insertionDate": "",
        "updateDate": ""
    }
  };

  /**
   * Retrieves list of centers for a specific distributor.
   *
   * @param distributorId -  ID of the distributor.
   * @returns An observable emitting a `CommonOption[]` object containing the list of center.
   */
  getCenters(
    distributorId?: string,
  ): Observable<CommonOption[]> {
    const params: GetCentersParam = {
      "distributorId": distributorId,
    };
    const centersCount$ = this.dataAdministrationService.getCenters(
      params,
    ) as Observable<CommonOption[]>;

    return centersCount$.pipe(
      this.errorService.handleError<CommonOption[]>({
        errorKey: ERROR_KEY('ERROR_CENTER_LIST'),
        fallbackValue: [],
      }),
    );
  }

  /**
   * Retrieves list of trafos for a specific center.
   *
   * @param centerId - Optional string representing the ID of the center.
   * @returns An observable emitting a `CommonOption[]` object containing the list of trafos.
   */
  getTrafos(
    centerId?: string,
  ): Observable<CommonOption[]> {
    const params: GetTrafosParam = {
      "centerId": centerId,
    };
    const trafosCount$ = this.dataAdministrationService.getTrafos(
      params,
    ) as Observable<CommonOption[]>;

    return trafosCount$.pipe(
      this.errorService.handleError<CommonOption[]>({
        errorKey: ERROR_KEY('ERROR_TRAFOS_LIST'),
        fallbackValue: [],
      }),
    );
  }

  /**
   * Retrieves list of lines for a specific center.
   *
   * @param centerId - string representing the ID of the center.
   * @param transformerId - string representing the ID of the trafo.
   * @returns An observable emitting a `CommonOption[]` object containing the list of lines.
   */
  getLines(
    centerId: string,
    transformerId: string,
  ): Observable<RegularOption[]> {
    const params: GetLinesParam = {
      "centerId": centerId,
      "transformerId": transformerId,
    };
    const trafosCount$ = this.dataAdministrationService.getLines(
      params,
    ) as Observable<RegularOption[]>;

    return trafosCount$.pipe(
      map((res) =>
        res.map((line: any) => ({
          value: line.id,
          label: line.name,
        }))
      ),
      this.errorService.handleError<RegularOption[]>({
        errorKey: ERROR_KEY('ERROR_LINES_LIST'),
        fallbackValue: [],
      }),
    );
  }

  /**
   * Retrieves default values of data for a specific distributors.
   *
   * @param deviceType - string representing the ID of the device.
   * @param distributorIds - string representing the ID of the distributor.
   * @returns An observable emitting a `CommonOption[]` object containing the defalut info for distributor.
   */
  getDataDistributorConfig(
    deviceType: string,
    distributorIds: string,
  ): Observable<GetDistributorDefaultResponse> {
    const params: GetDistributorDefalutParam = {
      distributorIds: distributorIds,
      deviceType: deviceType,
    };
    const dataDeistributor$ = this.dataAdministrationService.getDataDistributorConfig(
      params,
    ) as Observable<GetDistributorDefaultResponse>;

    return dataDeistributor$
    // .pipe(
    //   this.errorService.handleError<GetDistributorDefaultResponse>({
    //     errorKey: ERROR_KEY('ERROR_DISTRIBUTOR_DEFAULT'),
    //     fallbackValue: this.fallbackValueGetDistributorDefaultResponse,
    //   }),
    // );
  }

  /**
   * Update default values of data for a specific distributor.
   *
   * @param deviceType - string representing the ID of the device.
   * @param distributorIds - string representing the ID of the distributor.
   * @param body - List of config
   * @returns An observable emitting a `CommonOption[]` object containing the defalut info for distributor.
   */
  updateDataDistributorConfig(
    deviceType: string,
    distributorIds: string,
    body: bodyToConfig,
  ): Observable<any> {
    const params: UpdateDataDistributorConfigParam = {
      distributorIds: distributorIds,
      deviceType: deviceType,
      body: body,
    };
    // For update operations, let errors propagate to the interceptor
    return this.dataAdministrationService.updateDataDistributorConfig(params);
  }

  /**
   * Create a new data distributor configuration.
   *
   * @param body - Configuration data to add.
   * @returns An observable emitting the response.
   */
  addDataDistributorConfig(
    body: any,
  ): Observable<any> {
    const params: any = {
      body: body,
    };
    // For add operations, let errors propagate to the interceptor
    return this.dataAdministrationService.addDataDistributorConfig(params);
  }

  /**
   * Retrieves the information of the specific center configuration.
   *
   * @param centerSaveId - Optional string representing the ID of the center.
   * @returns An observable emitting a `GetDistributorDefaultResponse` object containing the information of the specific center configuration.
   */
  getCenterConfig(
    centerSaveId?: string,
  ): Observable<GetDistributorDefaultResponse> {
    let params: string = '';
    if (centerSaveId !== undefined) {
      params = centerSaveId;
    }
    const centerConfigSaved$ = this.dataAdministrationService.getCenterConfig(
      params,
    ) as Observable<GetDistributorDefaultResponse>;

    return centerConfigSaved$.pipe(
      this.errorService.handleError<GetDistributorDefaultResponse>({
        errorKey: ERROR_KEY('ERROR_SAVE_CENTER'),
        fallbackValue: this.fallbackValueGetDistributorDefaultResponse,
      }),
    );
  }

  /**
   * Save a specific center configuration.
   *
   * @param body - Objet with the body of values to save a center configuration.
   * @returns An observable emitting a `GetDistributorDefaultResponse` object containing the information saved center configuration.
   */
  saveCenterConfig(
    body?: any,
  ): Observable<GetDistributorDefaultResponse> {
    // For save operations, we don't want fallback values as they would be misleading
    // Let errors propagate to the interceptor for proper error handling
    return this.dataAdministrationService.saveCenterConfig(body);
  }

  /**
   * Update the information of the specific center configuration.
   *
   * @param body - Objet with the body of values to save a center configuration.
   * @param centerSaveId - Optional string representing the ID of the center.
   * @returns An observable emitting a `GetDistributorDefaultResponse` object containing the information of the specific center configuration.
   */
  updateSavedCenterConfig(
    body?: bodyToConfig,
    centerSaveId?: string,
  ): Observable<GetDistributorDefaultResponse> {
    // For update operations, we don't want fallback values as they would be misleading
    // Let errors propagate to the interceptor for proper error handling
    return this.dataAdministrationService.updateSavedCenterConfig(body, centerSaveId);
  }

  /**
   * Retrieves list of all centeres saved.
   *
   * @param deviceType - Optional string representing the ID of the center.
   * @returns An observable emitting a `CommonOption[]` object containing the list of trafos.
   */
  getAllCenterConfig(
    deviceType?: string,
    centerId?: string,
    filters?: Filter[],
    pageSize?: number,
    pageIndex?: number,
  ): Observable<any> {
    const params: any = {
      deviceType,
      filters,
      centerId,
      pageSize,
      pageIndex,
    };
    const centerConfigSaved$ = this.dataAdministrationService.getAllCenterConfig(
      params,
    ) as Observable<any>;

    return centerConfigSaved$.pipe(
      this.errorService.handleError<any[]>({
        errorKey: ERROR_KEY('ERROR_EVENTS_COUNT'),
        fallbackValue: [],
      }),
    );
  }


  /**
   * Enable a specific center.
   *
   * @param alarmConfigIds - array of string representing the ID of the alarmConfigIds.
   * @returns An observable emitting a true value
   */
  enableConfig(
    alarmConfigIds: string[],
  ): Observable<any[]> {
    const params: AlarmConfigParam = {
      alarmConfigIds: alarmConfigIds,
    };
    const trafosCount$ = this.dataAdministrationService.enableConfig(
      params,
    ) as Observable<any[]>;

    return trafosCount$.pipe(
      this.errorService.handleError<any[]>({
        errorKey: ERROR_KEY('ERROR_EVENTS_COUNT'),
        fallbackValue: [],
      }),
    );
  }

  /**
   * Enable a specific center.
   *
   * @param alarmConfigIds - array of string representing the ID of the alarmConfigIds.
   * @returns An observable emitting a true value
   */
  disableConfig(
    alarmConfigIds: string[],
  ): Observable<any[]> {
    const params: AlarmConfigParam = {
      alarmConfigIds: alarmConfigIds,
    };
    const trafosCount$ = this.dataAdministrationService.disableConfig(
      params,
    ) as Observable<any[]>;

    return trafosCount$.pipe(
      this.errorService.handleError<any[]>({
        errorKey: ERROR_KEY('ERROR_EVENTS_COUNT'),
        fallbackValue: [],
      }),
    );
  }

  /**
   * Delete center configurations by their IDs.
   *
   * @param alarmConfigIds - array of string representing the ID of the alarmConfigIds.
   * @returns An observable emitting the deletion result.
   */
  deleteConfig(
    alarmConfigIds: string[],
  ): Observable<any[]> {
    const body: AlarmConfigParam = {
      alarmConfigIds: alarmConfigIds,
    };
    // For delete operations, let errors propagate to the interceptor
    return this.dataAdministrationService.deleteConfig(body);
  }

}
